import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useAdminList } from "@/api/generated/endpoints/api"
import type { AdminList200AdminsItem } from "@/api/generated/models"
import { Combobox, type ComboboxOption } from "@/components/ui/combobox"

export interface OcAssigneeOption {
  value: string
  label: string
}

// ページネーション設定
const PAGE_SIZE = 20

interface OcAssigneeSelectorProps {
  /** 選択された担当者ID */
  value: string
  /** 値変更時のコールバック */
  onChange: (value: string) => void
  /** エラーメッセージ */
  error?: string
  /** プレースホルダー */
  placeholder?: string
  /** 無効状態 */
  disabled?: boolean
  /** CSSクラス */
  className?: string
}

export function OcAssigneeSelector({
  value,
  onChange,
  error,
  placeholder = "選択してください",
  disabled = false,
  className,
}: OcAssigneeSelectorProps) {
  // 検索・ページネーション状態
  const [search, setSearch] = useState("")
  const [page, setPage] = useState(1)
  const [items, setItems] = useState<AdminList200AdminsItem[]>([])

  // デバウンス用タイマー
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // デバウンスタイマーのクリーンアップ
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [])

  // 管理者API
  const { data: adminsData, isLoading } = useAdminList(
    { nameLike: search || undefined, page, limit: PAGE_SIZE },
    { query: { enabled: true } }
  )

  // APIデータ取得時にアイテムを蓄積
  useEffect(() => {
    if (adminsData?.admins) {
      setItems((prev) =>
        page === 1
          ? adminsData.admins
          : [...prev, ...adminsData.admins.filter((a) => !prev.some((p) => p.id === a.id))]
      )
    }
  }, [adminsData, page])

  // デバウンス付き検索ハンドラ
  const handleSearchChange = useCallback((value: string) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }
    debounceTimerRef.current = setTimeout(() => {
      setSearch(value)
      setPage(1)
    }, 300)
  }, [])

  // 追加読み込みハンドラ
  const handleLoadMore = useCallback(() => {
    setPage((p) => p + 1)
  }, [])

  // Combobox開いた時のリセットハンドラ
  const handleOpen = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }
    setSearch("")
    setPage(1)
  }, [])

  // オプション変換
  const comboboxOptions = useMemo<ComboboxOption[]>(
    () =>
      items.map((item) => ({
        value: item.id,
        label: item.name,
      })),
    [items]
  )

  const hasMore = (adminsData?.admins?.length ?? 0) === PAGE_SIZE

  return (
    <div className={className}>
      <Combobox
        value={value}
        onChange={onChange}
        options={comboboxOptions}
        placeholder={placeholder}
        searchPlaceholder="検索..."
        emptyMessage="該当なし"
        disabled={disabled}
        className="w-full h-[32px]"
        onSearchChange={handleSearchChange}
        onLoadMore={handleLoadMore}
        isLoading={isLoading}
        hasMore={hasMore}
        onOpen={handleOpen}
      />
      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
    </div>
  )
}
